import { BuildingPeriodAccountingExpense } from '@/app/models/accountingExpense';
import formatterMonetaryValue from '@/app/utils/numberUtils';
import { Data, getComparator, Order } from '@/pages/buildingsTotal';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface ExpensesTotalTableProps {
  expenses: BuildingPeriodAccountingExpense[];
}

function ExpensesTotalTable(props: ExpensesTotalTableProps) {
  const { expenses } = props;
  const { t } = useTranslation(['buildingsTotal', 'common']);

  const [orderExpenses, setOrderExpenses] = React.useState<Order>('desc');
  const [orderByExpenses, setOrderByExpenses] = React.useState<keyof Data>('value');

  const handleRequestSortExpenses = (property: keyof Data) => {
    const isAsc = orderByExpenses === property && orderExpenses === 'asc';
    setOrderExpenses(isAsc ? 'desc' : 'asc');
    setOrderByExpenses(property);
  };

  const visibleExpenses = React.useMemo(() => {
    if (!expenses) return [];
    const stabilized = [...expenses];
    return stabilized.sort((a, b) => getComparator(orderExpenses, 'value')(a, b));
  }, [expenses, orderExpenses]);

  return (
    <div style={{ width: '40%', marginLeft: '10rem' }}>
      <TableContainer sx={{ height: '25rem' }} component={Paper}>
        <Table stickyHeader size="small" aria-label="Expense Table">
          <TableHead sx={{ fontWeight: 700 }}>
            <TableRow>
              <TableCell align="left" style={{ fontWeight: 'bold' }}>
                {t('expense')}
              </TableCell>
              <TableCell align="left" style={{ fontWeight: 'bold' }}>
                <TableSortLabel
                  active={orderByExpenses === 'value'}
                  direction={orderByExpenses === 'value' ? orderExpenses : 'asc'}
                  onClick={() => handleRequestSortExpenses('value')}
                  style={{ fontWeight: 'bold' }}
                >
                  {t('value')}
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {expenses &&
              visibleExpenses.map((row: BuildingPeriodAccountingExpense) => (
                <TableRow key={row.id}>
                  <TableCell>{row.description}</TableCell>
                  <TableCell>{`R$ ${formatterMonetaryValue(row.value)}`}</TableCell>
                </TableRow>
              ))}
            {expenses?.length === 0 && (
              <TableRow>
                <TableCell sx={{ textAlign: 'center' }} colSpan={8}>
                  {t('common:no.results.found')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <div style={{ width: '100%', display: 'flex', flexDirection: 'row-reverse' }}>
        <Typography sx={{ alignSelf: 'end', marginTop: '1rem', marginRight: '5rem', fontWeight: 'bold' }}>
          {`${t('Total')}: R$ ${formatterMonetaryValue(
            (expenses?.reduce((acc, item) => acc + item.value, 0) as number) || 0,
          )}`}
        </Typography>
      </div>
    </div>
  );
}

export default ExpensesTotalTable;
